import { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { UserDetailsContext } from '../context/user-details/user-details'
import FactorYearReviewService from '../shared/services/factor-year-review/factor-year-review.service';
import { IFactorYearReviewItem } from '../shared/services/factor-year-review/factor-year-review.model';
import { FactorYearReviewDialog } from '../components/dialogs/settings/report-period/factor-year-review-dialog';
import { usePdfExportMode } from '../hooks/use-pdf-export-mode';
import gaiaTheme from './theme';

const periodLabel = (item: IFactorYearReviewItem): string => {
  const period = item.reportPeriod;
  if (!period) return 'a reporting period';
  if (period.friendlyName) return period.friendlyName;
  if (period.startDate && period.endDate) {
    return `${period.startDate.slice(0, 10)} to ${period.endDate.slice(0, 10)}`;
  }
  return period.calendarYear ? String(period.calendarYear) : 'a reporting period';
};

// FY-6: shown to org admins while open factor-year review items exist: a newly
// activated factor year supersedes provisional calculations in one of their
// reporting periods. Review opens the review-and-apply dialog in place;
// Dismiss is persisted server-side so it stays dismissed across sessions.
// onVisibilityChange lets the layout suppress sibling fixed banners (they share
// the same top offset and would overlap).
// leftOffset: width in px of the left rail chrome. The rail's Drawer paints above this
// fixed banner, so the banner starts at the rail's right edge and centres its content in
// the visible area. 0 on mobile / wherever the rail is hidden.
export const FactorYearReviewBanner = ({ onVisibilityChange, leftOffset = 0 }: { onVisibilityChange?: (visible: boolean) => void, leftOffset?: number }) => {
  const location = useLocation();
  const pdfExportMode = usePdfExportMode();
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const userDetailsContext = useContext(UserDetailsContext);
  const [items, setItems] = useState<IFactorYearReviewItem[]>([]);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const factorYearReviewService = new FactorYearReviewService();

  const isAdmin = userDetailsContext.userDetails?.roles?.includes('admin') ?? false;

  const loadItems = useCallback(() => {
    factorYearReviewService.getOpenItems()
      .then(response => setItems(response.items || []))
      .catch(() => setItems([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadItems();
    } else {
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, userDetailsContext.userDetails?.organisation?.id]);

  // Periods locked since discovery cannot be updated; do not nag about them.
  const visibleItems = items.filter(existing => !existing.reportPeriod?.locked);
  const isVisible = !pdfExportMode && isAdmin && visibleItems.length > 0
    // Hidden on the destination page itself: the period settings already show the state.
    && !location.pathname.startsWith('/settings/company-settings/reporting-periods/');

  useEffect(() => {
    onVisibilityChange?.(isVisible);
    return () => onVisibilityChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  if (!isVisible) return null;

  const item = visibleItems[0];
  const sourceName = item.conversionSource?.source ?? 'conversion factor';
  // Internal staff browsing as a customer must not silently suppress the org's
  // notification: dismissal belongs to the org's own admins.
  const canDismiss = !userDetailsContext.userDetails?.isLoggedInAsCustomer;

  const handleDismiss = () => {
    factorYearReviewService.dismiss(item.id)
      .then(() => setItems(previous => previous.filter(existing => existing.id !== item.id)))
      .catch(() => undefined);
  };

  return (
    // Fixed below the AppBar, matching the sibling banners' geometry (48px bar,
    // top offset 64 / 106 on mobile, centered content). The in-flow spacer
    // reserves the same height so page content clears the banner instead of
    // sitting underneath it.
    <>
      <Box sx={{
        height: isMobileMode ? '70px' : '48px',
        width: `calc(100% - ${leftOffset}px)`,
        backgroundColor: 'secondary.light',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        zIndex: 1000,
        top: isMobileMode ? 106 : 64,
        left: `${leftOffset}px`,
        px: 3,
        gap: 2,
      }}>
        <Typography variant='body1' noWrap={!isMobileMode} sx={{ color: 'text.primary', minWidth: 0 }}>
          New {sourceName} {item.year} factors are available: {item.provisionalCount} calculation{item.provisionalCount === 1 ? '' : 's'} in {periodLabel(item)} use provisional factors.
          {visibleItems.length > 1 ? ` (+${visibleItems.length - 1} more)` : ''}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Button size='small' variant='contained' disableElevation onClick={() => setReviewDialogOpen(true)}>
            Review
          </Button>
          {canDismiss && (
            // The theme defaults buttons to contained; Dismiss is the quiet action.
            <Button size='small' variant='text' onClick={handleDismiss} sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}>
              Dismiss
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={{ height: isMobileMode ? '70px' : '48px', width: '100%' }} />
      <FactorYearReviewDialog
        open={reviewDialogOpen}
        item={item}
        onClose={() => setReviewDialogOpen(false)}
        onApplied={(itemId) => {
          // The worker marks the item actioned when the job completes; drop it
          // locally so the banner clears immediately. The user stays on their
          // current page; the snackbar reports recalculation progress.
          setItems(previous => previous.filter(existing => existing.id !== itemId));
        }}
      />
    </>
  );
};
